import firebase from "firebase/app";
import { useCallback, useRef, useState } from "react";

import "firebase/auth";

/** SDK が読み込む grecaptcha のうち、ここで使う分だけ */
type Grecaptcha = { reset: (widgetId: number) => void };

declare global {
  // グローバルな型の拡張（declaration merging）にはinterfaceが必要なので以下を無視する
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

/**
 * 送信先の番号は codeSent のときにしか存在しないため、状態に持たせて
 * 「codeSent なのに番号が無い」を型で作れないようにする。
 */
type LinkPhoneNumberState =
  | { step: "input" }
  | { step: "codeSent"; sentPhoneNumber: string }
  | { step: "completed" };

/**
 * 新規の紐付けは linkWithPhoneNumber が ConfirmationResult を返すが、
 * 既に番号があるときは差し替えになり、verifyPhoneNumber が verificationId を
 * 返す。どちらか一方しか持たないので、まとめて1つで扱う。
 */
type PendingVerification =
  | { method: "link"; confirmationResult: firebase.auth.ConfirmationResult }
  | { method: "update"; verificationId: string };

/** 直近に送ったコードの宛先と時刻。再送の間隔を測るのに使う */
export type LastSentCode = { phoneNumber: string; sentAt: number };

export type LinkPhoneNumberError =
  | "invalidPhoneNumber"
  | "invalidCode"
  | "codeExpired"
  | "alreadyInUse"
  | "alreadyLinked"
  | "tooManyRequests"
  | "recaptchaFailed"
  | "requiresRecentLogin"
  | "unavailable"
  | "unknown";

const toLinkPhoneNumberError = (error: unknown): LinkPhoneNumberError => {
  const errorCode =
    typeof error === "object" && error !== null && "code" in error
      ? String(error.code)
      : "";
  switch (errorCode) {
    case "auth/invalid-phone-number":
    case "auth/missing-phone-number":
      return "invalidPhoneNumber";
    case "auth/invalid-verification-code":
    case "auth/missing-verification-code":
    case "auth/invalid-verification-id":
      return "invalidCode";
    // 番号の変更は直近のログインを求められる。再認証はこのフックの担当外
    case "auth/requires-recent-login":
      return "requiresRecentLogin";
    case "auth/code-expired":
      return "codeExpired";
    case "auth/credential-already-in-use":
    case "auth/account-exists-with-different-credential":
      return "alreadyInUse";
    case "auth/provider-already-linked":
      return "alreadyLinked";
    case "auth/too-many-requests":
    case "auth/quota-exceeded":
      return "tooManyRequests";
    case "auth/operation-not-allowed":
    case "auth/unauthorized-domain":
      return "unavailable";
    case "auth/invalid-app-credential":
    case "auth/captcha-check-failed":
      return "recaptchaFailed";
    default:
      return "unknown";
  }
};

/**
 * 電話番号単体でのログインは提供しないため、sign in ではなく linkWithPhoneNumber を使う。
 * reCAPTCHA は Firebase Authentication の Phone provider が必須とするため、
 * recaptchaContainerId で指定した要素に invisible reCAPTCHA を描画する。
 */
/**
 * grecaptcha は描画先の要素を覚えていて、同じ要素へ2度目を描画すると
 * "reCAPTCHA has already been rendered in this element" で失敗する。この例外は
 * Firebase の error code を持たないため、原因の分からない失敗として表に出る。
 *
 * フォームは認証のたびにアンマウントされるので、フックの寿命で持つと作り直しに
 * なる。描画先の要素ごとに使い回す。要素が消えれば WeakMap から落ちる。
 */
const recaptchaVerifiers = new WeakMap<
  Element,
  firebase.auth.RecaptchaVerifier
>();

const getRecaptchaVerifier = (
  recaptchaContainerId: string
): firebase.auth.RecaptchaVerifier => {
  const container = document.getElementById(recaptchaContainerId);
  if (container === null) {
    // 描画先を用意するのは呼び出し側。無ければ SDK 側で失敗させる
    return new firebase.auth.RecaptchaVerifier(recaptchaContainerId, {
      size: "invisible",
    });
  }
  const cached = recaptchaVerifiers.get(container);
  if (cached !== undefined) {
    return cached;
  }
  const created = new firebase.auth.RecaptchaVerifier(recaptchaContainerId, {
    size: "invisible",
  });
  recaptchaVerifiers.set(container, created);
  return created;
};

export const useLinkPhoneNumber = (recaptchaContainerId: string) => {
  const [verificationState, setVerificationState] =
    useState<LinkPhoneNumberState>({ step: "input" });
  const [error, setError] = useState<LinkPhoneNumberError | undefined>(
    undefined
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSentCode, setLastSentCode] = useState<LastSentCode | undefined>(
    undefined
  );

  const pendingVerificationRef = useRef<PendingVerification | undefined>(
    undefined
  );

  const prepareRecaptchaVerifier = useCallback(async () => {
    const recaptchaVerifier = getRecaptchaVerifier(recaptchaContainerId);
    // render() は描画済みなら同じ widget の id を返す
    const widgetId = await recaptchaVerifier.render();
    // トークンは1回しか使えないので、使い回すだけでは2回目の送信が通らない
    window.grecaptcha?.reset(widgetId);
    return recaptchaVerifier;
  }, [recaptchaContainerId]);

  const requestVerificationCode = useCallback(
    async (phoneNumber: string) => {
      setError(undefined);

      const { currentUser } = firebase.auth();
      if (currentUser === null) {
        setError("unknown");
        return;
      }

      setIsSubmitting(true);
      try {
        const recaptchaVerifier = await prepareRecaptchaVerifier();

        if (currentUser.phoneNumber === null) {
          pendingVerificationRef.current = {
            method: "link",
            confirmationResult: await currentUser.linkWithPhoneNumber(
              phoneNumber,
              recaptchaVerifier
            ),
          };
        } else {
          // 紐付け済みの番号を差し替える経路。link は provider-already-linked になる
          pendingVerificationRef.current = {
            method: "update",
            verificationId:
              await new firebase.auth.PhoneAuthProvider().verifyPhoneNumber(
                phoneNumber,
                recaptchaVerifier
              ),
          };
        }
        setLastSentCode({ phoneNumber, sentAt: Date.now() });
        setVerificationState({
          step: "codeSent",
          sentPhoneNumber: phoneNumber,
        });
      } catch (e) {
        console.error(e);
        setError(toLinkPhoneNumberError(e));
      } finally {
        setIsSubmitting(false);
      }
    },
    [prepareRecaptchaVerifier]
  );

  const confirmVerificationCode = useCallback(
    async (verificationCode: string): Promise<boolean> => {
      setError(undefined);

      const pendingVerification = pendingVerificationRef.current;
      if (pendingVerification === undefined) {
        setError("unknown");
        return false;
      }

      setIsSubmitting(true);
      try {
        if (pendingVerification.method === "link") {
          await pendingVerification.confirmationResult.confirm(
            verificationCode
          );
        } else {
          const { currentUser } = firebase.auth();
          if (currentUser === null) {
            setError("unknown");
            return false;
          }
          const credential = firebase.auth.PhoneAuthProvider.credential(
            pendingVerification.verificationId,
            verificationCode
          );
          await currentUser.updatePhoneNumber(credential);
        }
        pendingVerificationRef.current = undefined;
        setVerificationState({ step: "completed" });
        return true;
      } catch (e) {
        console.error(e);
        setError(toLinkPhoneNumberError(e));
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    []
  );

  const backToInput = useCallback(() => {
    pendingVerificationRef.current = undefined;
    setError(undefined);
    setVerificationState({ step: "input" });
  }, []);

  return {
    verificationState,
    error,
    isSubmitting,
    lastSentCode,
    requestVerificationCode,
    confirmVerificationCode,
    backToInput,
  };
};
